import { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Search, Users, RefreshCcw, ArrowRight, Download, Calendar, ChevronDown, ChevronRight,
  Tag, LayoutGrid, AlertTriangle, Loader2,
} from 'lucide-react';
import authFetch from '../utils/authFetch';
import formatRelative, { formatAbsolute } from '../utils/formatRelative';
import useToastStore from '../store/toastStore';
import {
  PERIOD_PRESETS, DEFAULT_PERIOD_PRESET, MAX_ORG_WINDOW_DAYS, resolvePeriod, buildEventsParams,
  groupEvents, describeBatchLabel, parseExportStatus, EXPORT_REASON_LABEL, filenameFromContentDisposition,
} from '../utils/eventSearch';

// M5b: esta tela consome GET /api/v1/events (+ /facets, /export.csv) — a
// busca de verdade no servidor (backend/src/routes/events.routes.js). Antes
// filtrava no navegador sobre os eventos já carregados (M4b); o contrato
// novo faz o recorte no banco, então texto/pessoa/ação/quadro nunca mais
// dependem do que já chegou na tela. scope=org sempre (é a busca global da
// organização — o quadro entra como FILTRO, não como troca de scope).

function initials(name) {
  return name ? name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?';
}

function Avatar({ name, url, size = 28 }) {
  return (
    <div
      className="rounded-full bg-accent/20 text-accent flex items-center justify-center font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
      title={name}
    >
      {url ? <img src={url} alt={name} className="w-full h-full rounded-full object-cover" /> : initials(name)}
    </div>
  );
}

function ValueChangeBody({ details }) {
  const d = details;
  const name = d.column_name || 'coluna';
  const fmt = (v) => {
    if (v == null) return null;
    if (typeof v === 'object') return v.label || v.url || JSON.stringify(v);
    return String(v);
  };
  const fromLabel = fmt(d.from);
  const toLabel = fmt(d.to);
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span>alterou <strong>{name}</strong>:</span>
      <em>{fromLabel ?? '—'}</em>
      <ArrowRight className="w-3 h-3 text-text-secondary/60" />
      <em>{toLabel ?? '—'}</em>
    </span>
  );
}

// M4b: nomes do catálogo (services/eventCatalog.js). `a.data` — GET /events
// não embute mais os detalhes em `details` (isso era só da rota legada
// /activity); o resto do mapeamento de ação é o mesmo de sempre. Ação fora
// desta lista (dezenas no catálogo completo — acesso, papéis, organização
// etc.) cai no fallback técnico: narrar cada uma é fora do escopo desta tela.
function describe(a) {
  const d = a.data || {};
  switch (a.action) {
    case 'card.created': return d.is_divider ? <>criou uma divisória</> : <>criou o item</>;
    case 'card.renamed': return <>renomeou de <strong>{d.from}</strong> para <strong>{d.to}</strong></>;
    case 'card.status.changed':
    case 'card.assignee.changed':
    case 'card.value.updated':
    case 'card.value.cleared':
      return <ValueChangeBody details={d} />;
    case 'comment.created': {
      const attachSuffix = d.attachment_count ? <> (com {d.attachment_count} anexo{d.attachment_count > 1 ? 's' : ''})</> : '';
      return d.parent_id ? <>respondeu{attachSuffix}</> : <>comentou{attachSuffix}</>;
    }
    case 'attachment.uploaded':
      return <>anexou <strong>{d.original_name}</strong></>;
    case 'attachment.deleted':
      return <>removeu anexo <strong>{d.original_name}</strong></>;
    case 'card.moved':
      return <>moveu de <strong>{d.from_group_name ?? `grupo ${d.from_group_id}`}</strong> para <strong>{d.to_group_name ?? `grupo ${d.to_group_id}`}</strong></>;
    case 'card.archived':
      return <>arquivou o item</>;
    case 'card.restored':
      return <>restaurou o item</>;
    case 'card.deleted':
      return <>excluiu o item</>;
    case 'group.moved':
      return <>moveu o grupo de <strong>{d.from_board_name ?? `quadro ${d.from_board_id}`}</strong> para <strong>{d.to_board_name ?? `quadro ${d.to_board_id}`}</strong></>;
    default:
      return <>ação: {a.action}</>;
  }
}

// --- dropdown genérico de filtro facetado (ação/pessoa/quadro) -------------

function FacetDropdown({ icon: Icon, label, selectedLabel, items, onSelect, renderLabel, emptyLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors hover:bg-white/5 ${selectedLabel ? 'text-accent' : 'text-text-primary'}`}
      >
        <Icon className="w-3.5 h-3.5" />
        <span className="max-w-[130px] truncate">{selectedLabel || label}</span>
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-64 bg-card border border-border rounded-lg shadow-xl py-1 z-30 max-h-72 overflow-y-auto">
          <button
            onMouseDown={(e) => { e.preventDefault(); onSelect(null); setOpen(false); }}
            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-white/5 ${!selectedLabel ? 'text-accent' : 'text-text-primary'}`}
          >
            Todos
          </button>
          {items.length === 0 && (
            <div className="px-3 py-2 text-xs text-text-secondary/70">{emptyLabel || 'Nada no recorte atual'}</div>
          )}
          {items.map((it) => (
            <button
              key={it.key}
              onMouseDown={(e) => { e.preventDefault(); onSelect(it.key); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-white/5 text-text-primary"
            >
              {renderLabel ? renderLabel(it) : <span className="truncate flex-1">{it.label}</span>}
              <span className="text-[11px] text-text-secondary/70 shrink-0">{it.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- seletor de período ------------------------------------------------------
// Padrão razoável (30 dias) sempre dentro do teto de 90 — facetas/texto livre
// em scope=org EXIGEM essa janela fechada (eventQuery.js#assertOrgWindow);
// como esta tela sempre pede facetas, o período nunca fica "sem janela".

function PeriodPicker({ preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, error }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const activeLabel = preset === 'custom'
    ? (customFrom ? `${customFrom}${customTo ? ` a ${customTo}` : ' até hoje'}` : 'Personalizado')
    : PERIOD_PRESETS.find((p) => p.key === preset)?.label;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md transition-colors hover:bg-white/5 ${error ? 'text-danger' : 'text-text-primary'}`}
      >
        <Calendar className="w-3.5 h-3.5" />
        {activeLabel}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 w-72 bg-card border border-border rounded-lg shadow-xl p-3 z-30 space-y-2">
          <div className="flex gap-1">
            {PERIOD_PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                className={`flex-1 text-xs px-2 py-1.5 rounded-md border transition-colors ${preset === p.key ? 'border-accent text-accent bg-accent/10' : 'border-border text-text-secondary hover:text-text-primary'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setPreset('custom')}
            className={`w-full text-xs px-2 py-1.5 rounded-md border transition-colors ${preset === 'custom' ? 'border-accent text-accent bg-accent/10' : 'border-border text-text-secondary hover:text-text-primary'}`}
          >
            Personalizado
          </button>
          {preset === 'custom' && (
            <div className="grid grid-cols-2 gap-2 pt-1">
              <label className="text-[11px] text-text-secondary">
                De
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="mt-0.5 w-full bg-main border border-border rounded px-2 py-1 text-xs text-text-primary outline-none focus:border-accent/50"
                />
              </label>
              <label className="text-[11px] text-text-secondary">
                Até
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="mt-0.5 w-full bg-main border border-border rounded px-2 py-1 text-xs text-text-primary outline-none focus:border-accent/50"
                />
              </label>
            </div>
          )}
          {error && (
            <div className="text-[11px] text-danger flex items-start gap-1 pt-1">
              <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
              {error}
            </div>
          )}
          <div className="text-[10px] text-text-secondary/60 pt-1">
            Máximo de {MAX_ORG_WINDOW_DAYS} dias para busca e filtros nesta tela.
          </div>
        </div>
      )}
    </div>
  );
}

const EVENTS_URL = '/api/v1/events';
const FACETS_URL = '/api/v1/events/facets';
const EXPORT_URL = '/api/v1/events/export.csv';
const PAGE_LIMIT = 50;
const POLL_MS = 10000;
const SEARCH_DEBOUNCE_MS = 350;

export default function GlobalActivityModal({ onClose }) {
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [periodPreset, setPeriodPreset] = useState(DEFAULT_PERIOD_PRESET);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [actionFilter, setActionFilter] = useState(null);
  const [actorFilter, setActorFilter] = useState(null); // { id, name }
  const [boardFilter, setBoardFilter] = useState(null); // { id, name }

  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [facets, setFacets] = useState({ by_action: [], by_actor: [], by_board: [] });
  const [expanded, setExpanded] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const [, setTick] = useState(0); // mantém o tooltip de tempo relativo fresco

  const showError = useToastStore((s) => s.showError);

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [qInput]);

  const period = useMemo(() => resolvePeriod(periodPreset, customFrom, customTo), [periodPreset, customFrom, customTo]);

  const filters = useMemo(() => ({
    q: q || undefined,
    from: period.from,
    to: period.to,
    action: actionFilter || undefined,
    actor: actorFilter?.id,
    board: boardFilter?.id,
  }), [q, period.from, period.to, actionFilter, actorFilter, boardFilter]);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const load = async () => {
    if (period.error) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = buildEventsParams(filters, { limit: PAGE_LIMIT });
      const res = await authFetch(`${EVENTS_URL}?${params.toString()}`, { full: true });
      setItems(res.data || []);
      setNextCursor(res.meta?.next_cursor ?? null);
      setHasMore(!!res.meta?.has_more);
    } catch (err) {
      setItems([]);
      setNextCursor(null);
      setHasMore(false);
      showError(err.message || 'Falha ao buscar eventos');
    } finally {
      setLoading(false);
    }
  };

  const loadFacets = async () => {
    if (period.error) return;
    try {
      const params = buildEventsParams(filters);
      const res = await authFetch(`${FACETS_URL}?${params.toString()}`, { full: true });
      setFacets(res.data || { by_action: [], by_actor: [], by_board: [] });
    } catch {
      // Facetas alimentam só os dropdowns — uma falha aqui não pode travar a lista principal.
      setFacets({ by_action: [], by_actor: [], by_board: [] });
    }
  };

  const loadMore = async () => {
    if (!hasMore || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = buildEventsParams(filters, { limit: PAGE_LIMIT, cursor: nextCursor });
      const res = await authFetch(`${EVENTS_URL}?${params.toString()}`, { full: true });
      setItems((prev) => [...prev, ...(res.data || [])]);
      setNextCursor(res.meta?.next_cursor ?? null);
      setHasMore(!!res.meta?.has_more);
    } catch (err) {
      showError(err.message || 'Falha ao carregar mais eventos');
    } finally {
      setLoadingMore(false);
    }
  };

  // Um único efeito por "receita de filtros": recarrega a primeira página +
  // facetas e reinicia o poll sempre que texto/período/ação/pessoa/quadro
  // mudar. O poll (mesma ideia dos modais anteriores) busca só a primeira
  // página com os filtros VIGENTES e junta por id no topo — nunca substitui,
  // senão o que "carregar mais" trouxe desapareceria a cada rodada.
  useEffect(() => {
    setExpanded(new Set());
    load();
    loadFacets();
    const timer = setInterval(async () => {
      if (period.error) return;
      try {
        const params = buildEventsParams(filters, { limit: PAGE_LIMIT });
        const res = await authFetch(`${EVENTS_URL}?${params.toString()}`, { full: true });
        const fresh = res.data || [];
        setItems((prev) => {
          const seen = new Set(prev.map((a) => a.id));
          const onlyNew = fresh.filter((a) => !seen.has(a.id));
          return onlyNew.length ? [...onlyNew, ...prev] : prev;
        });
      } catch { /* poll silencioso */ }
    }, POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    const ticker = setInterval(() => setTick((n) => n + 1), 10000);
    return () => clearInterval(ticker);
  }, []);

  const groups = useMemo(() => groupEvents(items), [items]);

  const actionItems = useMemo(
    () => (facets.by_action || []).map((f) => ({ key: f.action, label: f.action, count: f.count })),
    [facets]
  );
  const actorItems = useMemo(
    () => (facets.by_actor || [])
      .filter((f) => f.actor_user_id != null)
      .map((f) => ({ key: String(f.actor_user_id), label: f.actor_name || `Usuário ${f.actor_user_id}`, count: f.count })),
    [facets]
  );
  const boardItems = useMemo(
    () => (facets.by_board || [])
      .filter((f) => f.board_id != null)
      .map((f) => ({ key: String(f.board_id), label: f.board_name || `Quadro ${f.board_id}`, count: f.count })),
    [facets]
  );

  const toggleExpanded = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleExport = async () => {
    if (period.error) { showError(period.error); return; }
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = buildEventsParams(filters);
      const res = await fetch(`${EXPORT_URL}?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let msg = `Falha ao exportar (${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* corpo não era JSON */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const text = await blob.text();
      const status = parseExportStatus(text);

      const filename = filenameFromContentDisposition(res.headers.get('content-disposition'));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      // Linha de status ausente = arquivo cortado no transporte (ver
      // csvStatusRow em events.routes.js); outcome != completo = a
      // exportação parou antes do fim com o motivo em `reason`. Nos dois
      // casos o arquivo já baixou (o que saiu, saiu de verdade) — o aviso
      // só evita que alguém trate um CSV pela metade como o recorte inteiro.
      if (!status) {
        showError('O arquivo chegou sem a linha de status final — pode ter sido cortado no download. Confira antes de usar.');
      } else if (status.outcome !== 'completo') {
        const reason = EXPORT_REASON_LABEL[status.reason] || status.reason || 'motivo desconhecido';
        showError(`Exportação incompleta: ${reason}. ${status.count} linha(s) exportada(s).`);
      }
    } catch (err) {
      showError(err.message || 'Falha ao exportar eventos');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-[880px] max-w-[95vw] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-text-secondary">Registro</div>
            <h3 className="text-lg font-semibold text-text-primary">Busca de eventos</h3>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-border/50 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
              <input
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Pesquisar"
                className="w-full bg-main border border-border rounded-md pl-7 pr-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50"
              />
            </div>
            <PeriodPicker
              preset={periodPreset}
              setPreset={setPeriodPreset}
              customFrom={customFrom}
              setCustomFrom={setCustomFrom}
              customTo={customTo}
              setCustomTo={setCustomTo}
              error={period.error}
            />
            <button
              onClick={handleExport}
              disabled={exporting || !!period.error}
              title="Exportar CSV do recorte atual"
              className="flex items-center gap-1.5 text-sm text-text-primary hover:bg-white/5 px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Exportar
            </button>
            <button
              onClick={() => { load(); loadFacets(); }}
              className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-white/5 rounded transition-colors"
              title="Recarregar"
            >
              <RefreshCcw className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <FacetDropdown
              icon={Tag}
              label="Ação"
              selectedLabel={actionFilter}
              items={actionItems}
              onSelect={(key) => setActionFilter(key)}
              emptyLabel="Nenhuma ação no recorte atual"
            />
            <FacetDropdown
              icon={Users}
              label="Pessoa"
              selectedLabel={actorFilter?.name}
              items={actorItems}
              onSelect={(key) => setActorFilter(key ? { id: Number(key), name: actorItems.find((i) => i.key === key)?.label } : null)}
              renderLabel={(it) => (
                <>
                  <Avatar name={it.label} size={18} />
                  <span className="truncate flex-1">{it.label}</span>
                </>
              )}
              emptyLabel="Ninguém no recorte atual"
            />
            <FacetDropdown
              icon={LayoutGrid}
              label="Quadro"
              selectedLabel={boardFilter?.name}
              items={boardItems}
              onSelect={(key) => setBoardFilter(key ? { id: Number(key), name: boardItems.find((i) => i.key === key)?.label } : null)}
              emptyLabel="Nenhum quadro no recorte atual"
            />
          </div>

          {period.error && (
            <div className="text-[11px] text-danger flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> {period.error}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center py-8 text-sm text-text-secondary">Carregando...</div>
          ) : (
            <>
              {groups.length === 0 ? (
                <div className="text-center py-8 text-sm text-text-secondary">Nenhum evento encontrado.</div>
              ) : (
                <div className="space-y-4">
                  {groups.map((g) => {
                    const count = g.events.length;
                    // Lote de 1 (teoricamente possível — bulk-move com um só
                    // cartão selecionado) cai no ramo de evento único: contar
                    // "moveu 1 cartão" é menos informativo que a descrição
                    // normal, que já traz o card_title.
                    const isGroup = count > 1;
                    const isExpanded = expanded.has(g.key);
                    const isApprox = typeof g.time_quality === 'string' && g.time_quality.startsWith('legacy');
                    const first = g.events[0];

                    return (
                      <div key={g.key} className="flex items-start gap-3">
                        <div
                          className="w-32 shrink-0 pt-1 text-[11px] text-text-secondary leading-tight"
                          title={formatRelative(g.occurred_at)}
                        >
                          {formatAbsolute(g.occurred_at)}
                          {isApprox && (
                            <span
                              className="block text-[9px] uppercase tracking-wide text-warning/80 mt-0.5"
                              title="Horário aproximado — evento migrado (time_quality legacy_*)"
                            >
                              ~ aprox.
                            </span>
                          )}
                        </div>
                        <Avatar name={g.actor_name} url={g.actor_avatar_url} />
                        <div className="flex-1 min-w-0 text-sm">
                          {isGroup ? (
                            <>
                              <button
                                onClick={() => toggleExpanded(g.key)}
                                className="flex items-center gap-1.5 text-text-primary hover:text-accent transition-colors text-left"
                              >
                                {isExpanded ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                                <strong>{g.actor_name || 'Usuário'}</strong>
                                <span>{g.kind === 'batch' ? describeBatchLabel(g.action, count) : `alterou ${count} itens`}</span>
                              </button>
                              {isExpanded && (
                                <div className="mt-2 ml-5 space-y-1.5 text-xs text-text-secondary border-l border-border/40 pl-3">
                                  {g.events.map((e) => (
                                    <div key={e.id} className="flex items-center gap-2 flex-wrap">
                                      <span className="truncate max-w-[220px] text-text-primary">
                                        {e.card_title || e.target_name || e.board_name || '—'}
                                      </span>
                                      <span>·</span>
                                      <span>{describe(e)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-text-primary flex items-center gap-1.5 flex-wrap">
                              <strong>{g.actor_name || 'Usuário'}</strong>
                              <span>{describe(first)}</span>
                              {first.card_title && (
                                <>
                                  <span className="text-text-secondary">em</span>
                                  <span className="text-accent truncate max-w-[240px]">{first.card_title}</span>
                                </>
                              )}
                              {first.board_name && (
                                <>
                                  <span className="text-text-secondary/60">·</span>
                                  <span className="text-text-secondary text-[11px] truncate max-w-[180px]">{first.board_name}</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {hasMore && (
                <div className="pt-4 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="text-xs font-medium text-accent hover:text-accent/80 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-md hover:bg-white/5 transition-colors"
                  >
                    {loadingMore ? 'Carregando...' : 'Carregar mais'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
